/**
 * FlahaSOIL v2 — ScientificDisclaimer (Phase 10D, P0 print hardening).
 *
 * Print / PDF only: high-contrast light paper notes for professional
 * report output. Dark workspace notes must use SpecialNote instead —
 * never this cream Alert on the soil-intelligence chrome.
 */
import { Alert, AlertTitle, Typography, Box } from "@mui/material";
import { flahaSoilColors } from "../../../../theme/flahaSoilTheme";

export interface ScientificDisclaimerProps {
  title?: string;
  content: string;
  severity?: "info" | "warning";
  /**
   * `print` (default) — light paper for PDF / report preview.
   * `workspace` is intentionally not supported; use SpecialNote.
   */
  surface?: "print";
}

export function ScientificDisclaimer({
  title = "Scientific methodology note",
  content,
  severity = "info",
}: ScientificDisclaimerProps) {
  const isWarning = severity === "warning";

  return (
    <Box
      sx={{ my: 2, "@media print": { breakInside: "avoid" } }}
      data-note-surface="print"
    >
      <Alert
        severity={severity}
        sx={{
          // Always light paper — report preview forces a white canvas.
          color: flahaSoilColors.textPrimary,
          backgroundColor: isWarning
            ? "rgba(216, 132, 0, 0.12)"
            : flahaSoilColors.cream100,
          border: `1px solid ${
            isWarning ? flahaSoilColors.warning : flahaSoilColors.sand300
          }`,
          "& .MuiAlert-icon": {
            color: isWarning ? flahaSoilColors.warning : flahaSoilColors.blue700,
          },
          "& .MuiAlert-message": {
            width: "100%",
            color: flahaSoilColors.textPrimary,
          },
        }}
      >
        <AlertTitle
          sx={{
            fontWeight: 700,
            fontSize: "0.85rem",
            color: flahaSoilColors.textPrimary,
          }}
        >
          {title}
        </AlertTitle>
        <Typography
          variant="body2"
          sx={{
            display: "block",
            color: flahaSoilColors.textSecondary,
            lineHeight: 1.55,
            fontStyle: "normal",
          }}
        >
          {content}
        </Typography>
      </Alert>
    </Box>
  );
}
